import React, { useMemo, useState, useCallback } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "./WeeklyScheduleView.css"; // Reuse existing card styles
import { getCurrentWeekRange, toDateKey, formatDayLabel, toLocalInputValue } from "./DashboardUtils";
import { getExternalCalendarColor } from "../Calendar/CalendarUtils";
import { useDashboard } from "../../hooks/useDashboard";
import { useHolidays } from "../../hooks/useHolidays";
import { useWorkspaces } from "../../hooks/useWorkspaces";

// Interaction Hooks & Components
import { useNotes } from "../../hooks/useNotes";
import { useMealplanning } from "../../hooks/useMealplanning";
import { useCalendar } from "../../hooks/useCalendar";
import { useChecklists } from "../../hooks/useChecklists";
import { useRecipes } from "../../hooks/useRecipes";
import { useTasks } from "../../hooks/useTasks";
import { useDayMarkers } from "../../hooks/useDayMarkers";

import { useUserWorkspaceSettings } from "../../hooks/useUserWorkspaceSettings";
import useStore from "../../utils/store";

import CalendarEventDialog from "../Calendar/CalendarEventDialog";
import MealPlanEditDialog from "../Meals/MealPlanEditDialog";
import NoteDialog from "../Notes/NoteDialog";
import TaskCreateDialog from "../Tasks/TaskCreateDialog";
import RecipeView from "../Meals/RecipeView";

import { DeleteRecurringTaskDialog } from "../Tasks/DeleteRecurringTaskDialog";
import DayColumn from "./DayColumn";

import { Modal, Button } from "react-bootstrap";
import { ClipboardList } from "lucide-react";

const BUNDESLAND = { name: "Baden-Württemberg", code: "BW", apiCode: "DE-BW" };

// BUNDESLAND declaration removed (duplicate)

const WeeklyScheduleView = ({ auth, weekOffset, onNoteChanged, renderDayColumns, startDate, daysCount }) => {
    const workspaceId = localStorage.getItem("workspaceId");

    // When startDate/daysCount are provided, render that exact range.
    // Otherwise fall back to weekOffset-based Mon-Sun.
    const { rangeStart, rangeEnd, renderStart, renderCount } = useMemo(() => {
        if (startDate && daysCount) {
            const rs = new Date(startDate);
            rs.setHours(0, 0, 0, 0);
            const re = new Date(rs);
            re.setDate(re.getDate() + daysCount - 1);
            // Fetch a wider buffer (7 days before and after) to avoid refetch on every day step
            const fetchStart = new Date(rs);
            fetchStart.setDate(fetchStart.getDate() - 7);
            const fetchEnd = new Date(re);
            fetchEnd.setDate(fetchEnd.getDate() + 7);
            return { rangeStart: fetchStart, rangeEnd: fetchEnd, renderStart: rs, renderCount: daysCount };
        }
        const { monday, sunday } = getCurrentWeekRange(weekOffset);
        return { rangeStart: monday, rangeEnd: sunday, renderStart: monday, renderCount: 7 };
    }, [weekOffset, startDate, daysCount]);

    const startISO = rangeStart.toISOString();
    const endISO = rangeEnd.toISOString();
    const startKey = toDateKey(rangeStart);
    const endKey = toDateKey(rangeEnd);
    const year = renderStart.getFullYear();

    // Auto-scroll to today on mount
    // Auto-scroll to today
    // Auto-scroll logic moved below data hooks
    const scrollContainerRef = React.useRef(null);

    // Holidays
    const { publicHolidays, schoolHolidays } = useHolidays(year, BUNDESLAND);

    // Pro/Subscription
    const subscription = useStore((state) => state.subscription);
    const isPro = subscription?.plan_name === 'Premium' || subscription?.plan_name === 'Pro';

    // Data Hooks
    const { data: weeklyDataRaw, loading, reload } = useDashboard({ workspaceId, start: startKey, end: endKey, token: auth.user?.id_token });
    const { addNote, updateNote, deleteNote } = useNotes({ workspaceId, token: auth.user?.id_token });
    const { plans, addPlan, updatePlan, deletePlan } = useMealplanning({ workspaceId, token: auth.user?.id_token });
    const { addEvent, editEvent, removeEvent, modifyInstance, cancelInstance } = useCalendar({ workspaceId });
    const { checklists, handleCreateChecklist, handleAddItem, handleRemoveItem, handleToggleItem, handleUpdateItemDescription } = useChecklists({ workspaceId, token: auth.user?.id_token });
    const { getRecipeById, recipes } = useRecipes({ token: auth.user?.id_token });
    const { updateTask, deleteTask: deleteProjectTask, createTask: createProjectTask, fetchProjects, projects } = useTasks({ token: auth.user?.id_token, workspaceId });

    const { addMarker, deleteMarker } = useDayMarkers({ workspaceId, token: auth.user?.id_token });
    const { settings: wsSettings } = useUserWorkspaceSettings({ token: auth.user?.id_token, workspaceId });

    // Auto-scroll to today
    React.useEffect(() => {
        if (loading) return; // Wait until data is loaded and grid is rendered

        // slight delay to ensure rendering
        const timer = setTimeout(() => {
            if (scrollContainerRef.current) {
                const todayEl = scrollContainerRef.current.querySelector('.today-highlight');
                if (todayEl) {
                    // Start is often better for vertical list to put it at top, or center.
                    todayEl.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
                }
            }
        }, 300); // 300ms delay to allow layout to settle
        return () => clearTimeout(timer);
    }, [weekOffset, loading]); // Re-run when switching weeks or loading finishes



    // State for Dialogs
    const [editingItem, setEditingItem] = useState({ dayKey: "", type: "", id: null });
    const [editValue, setEditValue] = useState("");
    const [showEventDialog, setShowEventDialog] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);
    const [formEvent, setFormEvent] = useState({ title: "", description: "", startTime: "", endTime: "" });
    const [initialEventTimes, setInitialEventTimes] = useState(null);
    const [showNoteModal, setShowNoteModal] = useState(false);
    const [isEditingNote, setIsEditingNote] = useState(false);
    const [noteFormData, setNoteFormData] = useState({ title: "", content: "", noteDate: "", category: "Allgemein", isGeneral: false });
    const [showMealModal, setShowMealModal] = useState(false);
    const [mealFormData, setMealFormData] = useState({ id: null, notes: "", planDate: "" });
    const [showTaskModal, setShowTaskModal] = useState(false);
    const [newTaskTitle, setNewTaskTitle] = useState("");
    const [taskTargetDate, setTaskTargetDate] = useState("");
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showDeleteRecurringDialog, setShowDeleteRecurringDialog] = useState(false);
    const [taskToDelete, setTaskToDelete] = useState(null);
    const [selectedRecipe, setSelectedRecipe] = useState(null);
    const [showRecipeModal, setShowRecipeModal] = useState(false);
    const [members, setMembers] = useState([]);

    // Marker Dialog State
    const [showMarkerModal, setShowMarkerModal] = useState(false);
    const [markerFormData, setMarkerFormData] = useState({ date: "", label: "", color: "#fee2e2", exists: false });

    // Data Hooks
    const { getMembers } = useWorkspaces({ token: auth.user?.id_token });

    // Fetch members
    React.useEffect(() => {
        if (workspaceId) {
            getMembers(workspaceId).then(list => setMembers(list || []));
        }
    }, [workspaceId, getMembers]);

    // Ensure projects loaded
    React.useEffect(() => {
        if (isPro && workspaceId) fetchProjects();
    }, [isPro, workspaceId, fetchProjects]);

    const weeklyData = weeklyDataRaw || {};

    // --- Handlers ---
    const handleAdd = async (dayKey, type) => {
        if (type === "notes") {
            setNoteFormData({ title: "", content: "", noteDate: dayKey, category: "Allgemein", isGeneral: false });
            setIsEditingNote(false);
            setShowNoteModal(true);
        } else if (type === "meals") {
            setMealFormData({ workspaceId: Number(workspaceId), planDate: dayKey, notes: "", externalRecipeUrl: "", mealTimeCodeId: 5 });
            setShowMealModal(true);
        } else if (type === "tasks") {
            setTaskTargetDate(dayKey);
            if (isPro) {
                setShowCreateDialog(true);
            } else {
                setNewTaskTitle("");
                setShowTaskModal(true);
            }
        } else if (type === "events") {
            setEditingEvent(null);
            setInitialEventTimes({ start: `${dayKey}T08:00:00`, end: `${dayKey}T09:00:00` });
            setFormEvent({ title: "", description: "", startTime: "", endTime: "" });
            setShowEventDialog(true);
        } else if (type === "marker") {
            // Check if marker exists
            const existingMarker = weeklyData[dayKey]?.marker;
            setMarkerFormData({
                date: dayKey,
                label: existingMarker?.label || "",
                color: existingMarker?.color || "#fee2e2",
                exists: !!existingMarker
            });
            setShowMarkerModal(true);
        }
    };

    const handleDelete = async (type, item) => {
        try {
            if (type === "notes") {
                await deleteNote(item.id);
                if (onNoteChanged) onNoteChanged();
            } else if (type === "meals") {
                await deletePlan(item.id);
            } else if (type === "events") {
                item.parentId ? await cancelInstance({ workspaceId: item.workspaceId, parentId: item.parentId, originalStartTime: item.originalStartTime }, startISO, endISO) : await removeEvent(item.id, startISO, endISO);
            } else if (type === "tasks") {
                const isRolledOut = (typeof item.id === 'string' && item.id.startsWith("gen-"));
                // isRolledOut tasks can now be mutated. The backend handles materialization.
                if (item.recurrence_rule || item.recurrenceRule) {
                    setTaskToDelete(item);
                    setShowDeleteRecurringDialog(true);
                    return;
                }
                item.isProjectTask ? await deleteProjectTask(item.id) : await handleRemoveItem(item.id);
            }
            await reload();
        } catch (err) { console.error(err); }
    };

    const handleToggleTask = async (task, dayKey) => {
        try {
            if (task.isProjectTask) {
                await updateTask(task.id, { isCompleted: !task.isDone });
            } else {
                const dateTitle = new Date(dayKey).toLocaleDateString("en-US", { year: "numeric", month: "long" });
                const checklistForDay = checklists.find((c) => c.title === dateTitle);
                if (checklistForDay) {
                    await handleToggleItem(checklistForDay.id, task.id, task.isDone);
                }
            }
            await reload();
        } catch (e) { console.error(e); }
    };



    const handleToggleEvent = async (event) => {
        try {
            // Check if it is a recurring instance (id starts with 'gen-')
            const isRecurringInstance = typeof event.id === 'string' && event.id.startsWith('gen-');

            if (isRecurringInstance) {
                // For recurring instances, we must create an exception using modifyInstance
                // We need: workspaceId, parentId, originalStartTime, and the new data
                // The 'event' object from DayColumn should have these from the mapping logic
                if (!event.parentId || !event.originalStartTime) {
                    console.error("Cannot toggle recurring instance: missing parentId or originalStartTime", event);
                    return;
                }

                await modifyInstance(
                    {
                        workspaceId: Number(workspaceId),
                        parentId: event.parentId,
                        originalStartTime: event.originalStartTime,
                        ...event, // inherit other props
                        isCompleted: !event.isCompleted // toggle status
                    },
                    startISO,
                    endISO
                );
            } else {
                // Standard event update
                await editEvent(event.id, { isCompleted: !event.isCompleted }, startISO, endISO);
            }
            await reload();
        } catch (e) { console.error("Toggle event failed", e); }
    };

    const handleSaveInlineEdit = async () => {
        if (editValue && !editValue.trim()) return;
        const { type, id, dayKey } = editingItem;
        if (type === "tasks") {
            const dateTitle = new Date(dayKey).toLocaleDateString("en-US", { year: "numeric", month: "long" });
            const checklistForDay = checklists.find((c) => c.title === dateTitle);
            if (checklistForDay) {
                await handleUpdateItemDescription(checklistForDay.id, id, editValue);
            }
        }
        setEditingItem({ dayKey: "", type: "", id: null });
        setEditValue("");
        await reload();
    };

    const handleOpenRecipe = async (recipeId, servings = null) => {
        const found = await getRecipeById(recipeId);
        if (found) {
            if (servings) found.plannedServings = servings;
            setSelectedRecipe(found);
            setShowRecipeModal(true);
        } else {
            alert("Rezept nicht gefunden oder noch nicht geladen.");
        }
    };

    const handleMealRowClick = (dayKey, item) => {
        // Prioritize: 1. External Link, 2. Internal Recipe, 3. Edit Dialog
        if (item.original.externalRecipeUrl) {
            window.open(item.original.externalRecipeUrl, "_blank", "noopener,noreferrer");
            return;
        }

        const fullPlan = plans.find(p => p.id === item.original.id);
        const rId = fullPlan?.recipeId || item.original.recipeId || item.original.recipe_id;

        if (rId) {
            handleOpenRecipe(rId, item.original.servings || fullPlan?.servings);
            return;
        }

        // Fallback to edit if no link
        startEditing(dayKey, 'meals', item.original);
    };

    const startEditing = (dayKey, type, item) => {
        if (type === "notes") {
            setNoteFormData({ ...item, noteDate: item.noteDate ? item.noteDate.split('T')[0] : dayKey });
            setIsEditingNote(true);
            setShowNoteModal(true);
        } else if (type === "meals") {
            const fullPlan = plans.find(p => p.id === item.id);
            setMealFormData({ ...item, ...(fullPlan || {}) });
            setShowMealModal(true);
        } else if (type === "events") {
            setEditingEvent(item);
            setFormEvent({ title: item.title, description: item.description || "", startTime: toLocalInputValue(item.startTime), endTime: toLocalInputValue(item.endTime) });
            setShowEventDialog(true);
        } else {
            setEditingItem({ dayKey, type, id: item.id });
            setEditValue(item.title || item.description || item.notes || "");
        }
    };

    const handleSaveTask = async () => {
        if (!newTaskTitle.trim() || !taskTargetDate) return;
        try {
            const dateTitle = new Date(taskTargetDate).toLocaleDateString("en-US", { year: "numeric", month: "long" });
            let checklistForDay = checklists.find((c) => c.title === dateTitle) || await handleCreateChecklist(dateTitle);
            await handleAddItem(checklistForDay.id, newTaskTitle, taskTargetDate);
            await reload();
            setShowTaskModal(false);
            setNewTaskTitle("");
        } catch (error) { console.error("Error creating task:", error); }
    }

    const handleProTaskCreate = async (taskData) => {
        let targetProjectId = projects && projects[0] ? projects[0].id : null;
        if (!targetProjectId) { console.warn("No project found"); return; }
        try {
            await createProjectTask({ ...taskData, projectId: targetProjectId, dueDate: taskData.dueDate || (taskTargetDate ? new Date(taskTargetDate).toISOString() : null) });
            await reload();
        } catch (e) { console.error(e); }
    };

    const handleDeleteInstance = async () => { if (taskToDelete) { await deleteProjectTask(taskToDelete.id, 'instance'); await reload(); setShowDeleteRecurringDialog(false); setTaskToDelete(null); } };
    const handleDeleteSeries = async () => { if (taskToDelete) { await deleteProjectTask(taskToDelete.id, 'series'); await reload(); setShowDeleteRecurringDialog(false); setTaskToDelete(null); } };

    const handleNoteSave = async () => {
        isEditingNote ? await updateNote(noteFormData.id, noteFormData) : await addNote({ ...noteFormData, workspaceId: Number(workspaceId) });
        await reload();
        if (onNoteChanged) await onNoteChanged();
        setShowNoteModal(false);
    };

    const handleSaveMarker = async () => {
        if (!markerFormData.label) return;
        try {
            await addMarker(markerFormData);
            await reload();
            setShowMarkerModal(false);
        } catch (err) {
            console.error(err);
        }
    };

    const handleCloseModal = useCallback(() => {
        setEditingEvent(null);
        setFormEvent({ title: "", description: "", startTime: "", endTime: "" });
        setShowEventDialog(false);
    }, []);


    // --- Data Preparation ---
    const daysToRender = renderCount;

    const days = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        return Array.from({ length: daysToRender }, (_, i) => {
            const d = new Date(renderStart);
            d.setDate(renderStart.getDate() + i);
            const key = toDateKey(d);
            const dayData = weeklyData[key] || {};

            // Build Schedule List (Meals + Events)
            let scheduleItems = [];
            let snacks = [];

            // 1. Events
            if (dayData.events) {
                dayData.events.forEach(ev => {
                    const start = new Date(ev.startTime);
                    const end = ev.endTime ? new Date(ev.endTime) : null;
                    const timeLabel = start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                    const endTimeLabel = end ? end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : null;

                    // Map linked profiles to members
                    const resolvedProfiles = (ev.linkedProfiles || []).map(lp => {
                        const member = members.find(m => m.id === lp.profileId);
                        return member ? { ...member, color: member.color || lp.color } : lp;
                    });

                    // Sync Color Logic
                    let syncColor = null;
                    if (ev.externalConfigId) {
                        const extTheme = getExternalCalendarColor(ev.externalConfigId);
                        if (extTheme) syncColor = extTheme.icon;
                    }

                    scheduleItems.push({
                        id: `event-${ev.id}`,
                        type: 'events',
                        time: timeLabel,
                        endTime: endTimeLabel,
                        sortTime: start.getTime(),
                        title: ev.title,
                        linkedProfiles: resolvedProfiles,
                        original: ev,
                        colorClass: '',
                        syncColor: syncColor
                    });
                });
            }

            // 2. Meals
            if (dayData.meals) {
                dayData.meals.forEach(meal => {
                    // Check codes: 1=Frühstück, 2=Mittagessen, 3=Abendessen, 4=Snack
                    const code = meal.mealTimeCodeId || meal.meal_time_code_id || 0;

                    if (code === 4) {
                        // Snack -> Todo section
                        snacks.push({
                            id: `meal-${meal.id}`,
                            type: 'meals', // Keep type meal to open meal dialog
                            title: meal.title || meal.notes || "Snack",
                            original: meal
                        });
                    } else {
                        // Schedule
                        let timeLabel = "Essen";
                        let sortHour = 18;

                        if (code === 1) { timeLabel = "Morgens"; sortHour = 8; }
                        else if (code === 2) { timeLabel = "Mittag"; sortHour = 12; }
                        else if (code === 3) { timeLabel = "Abend"; sortHour = 18; }
                        else {
                            // Fallback string match
                            const titleLower = (meal.title || meal.notes || "").toLowerCase();
                            if (titleLower.includes("frühstück") || titleLower.includes("breakfast") || titleLower.includes("morgens")) { timeLabel = "Morgens"; sortHour = 8; }
                            else if (titleLower.includes("mittag") || titleLower.includes("lunch")) { timeLabel = "Mittag"; sortHour = 12; }
                            else { timeLabel = "Abend"; sortHour = 18; }
                        }

                        // Create a sortTime based on day + hour
                        const sortTime = new Date(d);
                        sortTime.setHours(sortHour, 0, 0, 0);

                        // Checkify Recipe Check
                        const fullPlan = plans.find(p => p.id === meal.id);
                        const isCheckifyRecipe = !!(fullPlan?.recipeId || meal.recipeId || meal.recipe_id);

                        scheduleItems.push({
                            id: `meal-${meal.id}`,
                            type: 'meals',
                            time: timeLabel,
                            sortTime: sortTime.getTime(),
                            title: fullPlan?.recipeTitle || meal.title || fullPlan?.notes || meal.notes || "Geplantes Essen",
                            original: { ...meal, ...fullPlan }, // Merge ensures we have latest data
                            colorClass: 'text-warning',
                            isCheckifyRecipe: isCheckifyRecipe
                        });
                    }
                });
            }

            // Sort by time
            scheduleItems.sort((a, b) => a.sortTime - b.sortTime);

            // Tasks and Notes separate
            const tasks = dayData.tasks || [];
            const notes = dayData.notes || [];
            const marker = dayData.marker || null;

            // Holiday Check
            const isPublicHoliday = publicHolidays.includes(key);
            const isSchoolVacation = schoolHolidays.some((v) => {
                const startK = v.start.toLocaleDateString('sv-SE');
                const endK = v.end.toLocaleDateString('sv-SE');
                return key >= startK && key <= endK;
            });

            const isPast = d < today;

            return {
                date: d,
                key: key,
                label: formatDayLabel(d),
                scheduleItems,
                tasks,
                notes,
                snacks,
                marker,
                isPublicHoliday,
                isSchoolVacation,
                isPast
            };
        });
    }, [renderStart, weeklyData, publicHolidays, schoolHolidays, daysToRender, members]);

    const sharedHandlers = {
        handleAdd,
        handleDelete,
        handleToggleTask,
        handleToggleEvent,
        handleMealRowClick,
        startEditing,
        handleSaveInlineEdit,
        setEditValue
    };
    const editingState = { editingItem, editValue };

    const dayColumns = days.map(day => {
        const isToday = toDateKey(new Date()) === day.key;
        return (
            <DayColumn
                key={day.key}
                day={day}
                isToday={isToday}
                handlers={sharedHandlers}
                editingState={editingState}
                enableEventCompletion={wsSettings?.enableEventCompletion}
                members={members}
            />
        );
    });

    // Dialogs fragment — always rendered regardless of mode
    const dialogs = (
        <>
            <CalendarEventDialog
                show={showEventDialog}
                onClose={handleCloseModal}
                editingEvent={editingEvent}
                workspaceId={workspaceId}
                addEvent={addEvent}
                editEvent={editEvent}
                removeEvent={removeEvent}
                modifyInstance={modifyInstance}
                cancelInstance={cancelInstance}
                loadEvents={reload}
                currentRange={{ start: startISO, end: endISO }}
                initialTimes={initialEventTimes}
            />
            {
                showMealModal && (
                    <MealPlanEditDialog
                        formData={mealFormData}
                        setShowAddModal={setShowMealModal}
                        setFormData={setMealFormData}
                        handleSave={async () => {
                            mealFormData.id ? await updatePlan(mealFormData.id, mealFormData) : await addPlan({ ...mealFormData });
                            await reload();
                            setShowMealModal(false);
                        }}
                        recipes={recipes}
                    />
                )
            }
            <NoteDialog show={showNoteModal} isEditing={isEditingNote} formData={noteFormData} setFormData={setNoteFormData} onSave={handleNoteSave} onClose={() => setShowNoteModal(false)} />
            <Modal show={showTaskModal} onHide={() => setShowTaskModal(false)} centered size="sm">
                <Modal.Header closeButton border="0"><Modal.Title className="h6 fw-bold">Neues Todo</Modal.Title></Modal.Header>
                <Modal.Body>
                    <input type="text" className="form-control" placeholder="Todo eingeben..." autoFocus value={newTaskTitle} onChange={(e) => setNewTaskTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSaveTask()} />
                </Modal.Body>
                <Modal.Footer border="0">
                    <Button variant="light" size="sm" onClick={() => setShowTaskModal(false)}>Abbrechen</Button>
                    <Button variant="primary" size="sm" onClick={handleSaveTask}>Speichern</Button>
                </Modal.Footer>
            </Modal>

            {/* Marker Dialog */}
            <Modal show={showMarkerModal} onHide={() => setShowMarkerModal(false)} centered size="sm">
                <Modal.Header closeButton border="0"><Modal.Title className="h6 fw-bold">Tag markieren</Modal.Title></Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label className="form-label small fw-bold">Titel</label>
                        <input type="text" className="form-control" value={markerFormData.label} onChange={(e) => setMarkerFormData({ ...markerFormData, label: e.target.value })} placeholder="z.B. Geburtstag" autoFocus />
                    </div>
                    <div>
                        <label className="form-label small fw-bold">Farbe</label>
                        <div className="d-flex gap-2 flex-wrap">
                            {["#fee2e2", "#fef3c7", "#dcfce7", "#dbeafe", "#f3e8ff", "#fae8ff", "#ffffff"].map(c => (
                                <div
                                    key={c}
                                    onClick={() => setMarkerFormData({ ...markerFormData, color: c })}
                                    style={{ width: 30, height: 30, backgroundColor: c, cursor: 'pointer', border: markerFormData.color === c ? '2px solid #000' : '1px solid #ddd', borderRadius: '50%' }}
                                ></div>
                            ))}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer border="0">
                    {markerFormData.exists && (
                        <Button variant="outline-danger" size="sm" onClick={async () => {
                            await deleteMarker(markerFormData.date);
                            await reload();
                            setShowMarkerModal(false);
                        }} className="me-auto">Entfernen</Button>
                    )}
                    <Button variant="light" size="sm" onClick={() => setShowMarkerModal(false)}>Abbrechen</Button>
                    <Button variant="primary" size="sm" onClick={handleSaveMarker}>Speichern</Button>
                </Modal.Footer>
            </Modal>

            <TaskCreateDialog show={showCreateDialog} onHide={() => setShowCreateDialog(false)} onSubmit={handleProTaskCreate} initialDate={taskTargetDate} statusName="Dashboard" />
            <DeleteRecurringTaskDialog show={showDeleteRecurringDialog} onHide={() => setShowDeleteRecurringDialog(false)} onDeleteInstance={handleDeleteInstance} onDeleteSeries={handleDeleteSeries} />
            <Modal show={showRecipeModal} onHide={() => setShowRecipeModal(false)} fullscreen style={{ zIndex: 10000 }}>
                <Modal.Body className="p-0">
                    {selectedRecipe && <RecipeView recipe={selectedRecipe} initialServings={selectedRecipe.plannedServings} onBack={() => setShowRecipeModal(false)} />}
                </Modal.Body>
            </Modal>
        </>
    );

    // Carousel mode: parent controls layout, we just provide columns + dialogs
    if (renderDayColumns) {
        return (
            <>
                {renderDayColumns({ dayColumns, days, loading })}
                {dialogs}
            </>
        );
    }

    // Default grid mode
    if (loading) return <div className="p-5 text-center">Loading Schedule...</div>;

    return (
        <div className="container-fluid p-0 d-flex flex-column flex-grow-1">
            <div className="week-grid flex-grow-1 pb-4" ref={scrollContainerRef}>
                {dayColumns.map((col, i) => (
                    <div key={days[i].key} className="h-100">{col}</div>
                ))}
            </div>
            {dialogs}
        </div>
    );
};

export default WeeklyScheduleView;
